import {
  allSectionsOf,
  evaluateConditions,
  getInstanceCount,
  instanceCountKey,
  isStepEligibleForAudience,
  qualifiedKey,
  scopeValuesToInstance,
  sectionMinInstances,
  unitModeKey,
  withheldFieldIds,
  type StepAudience,
} from '@bmo/form-core';

import { isLayoutBlockType, type WorkflowField, type WorkflowTemplate } from '@/api/workflows';

import { type FieldValue } from './FieldRenderer';
import { hiddenBorrowedFieldIds } from './hiddenReveals';

/**
 * Drop every answer belonging to a field the advisor's current answers have
 * HIDDEN, ready to hand to PDF generation.
 *
 * Spec §6.4 ("Data behavior when something hides") is explicit: hidden values
 * are kept while the form is open so toggling a branch doesn't lose work, but
 * on submit they are "dropped — not sent to PDF, not sent to BMO". The second
 * half was implemented only on the BMO side. `buildApplicationSnapshot` gates
 * `plctData` by visibility, but the PDF preview/download path sent FormShell's
 * RAW answer map straight through, and `collectFilledFields` walks every
 * authored field without ever reading a condition. So an advisor who filled the
 * managed-account fields and then switched to unmanaged still got those values
 * printed into their mapped AcroForm boxes.
 *
 * ## Why a denylist
 *
 * This removes the keys of fields proven hidden and copies everything else
 * through untouched, rather than building up the set of keys to keep. An
 * allowlist would silently drop any key this walk doesn't know to re-add —
 * advisor-prefill slots, future bookkeeping sentinels — turning a narrow fix
 * into a broad one. A key this function has never heard of is not evidence that
 * it should be discarded.
 *
 * ## Visibility is read from the ORIGINAL map, in one pass
 *
 * Nothing is re-evaluated against the pruned result, so hiding does not cascade:
 * a field gated on an answer that is itself being dropped stays. That is
 * deliberate and load-bearing — `buildApplicationSnapshot` resolves visibility
 * the same way, and the moment the two disagree the PDF an advisor previews
 * stops matching the application BMO receives. Agreeing with the snapshot
 * matters more than any particular cascade rule.
 *
 * ## Audience counts as hidden too
 *
 * A step the originator marked `advisorOnly`, and every advisor-sourced field
 * anywhere, are withheld from the PUBLIC customer runtime — see
 * `publicStepVisibility`. They are dropped here for the same reason authored
 * conditions are: the customer never saw them, so their answers are not the
 * customer's and must not print into the customer's PDF. This is the obligation
 * `isPublicHiddenStep`'s doc comment names — `advisorOnly` is the first audience
 * rule that can hide a `DynamicForm` step, which is where answers live.
 *
 * Returns the input map unchanged (same reference) when nothing is hidden,
 * which is the common case.
 */
export function visibleValues(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  /** Who the answers were collected from. Defaults to `'internal'`, which drops
   *  nothing on audience grounds — the advisor runtime and both previews. */
  audience: StepAudience = 'internal',
): Record<string, FieldValue> {
  const drop = new Set<string>();
  const allSections = allSectionsOf(template);

  for (const step of template.steps) {
    // Only DynamicForm steps carry advisor answers.
    if (step.type !== 'DynamicForm') continue;
    // A hidden step takes everything under it, whatever its sections say. Audience
    // first and conditions second, exactly as FormShell's stepVisibility composes them.
    const stepHidden =
      !isStepEligibleForAudience(step, audience) ||
      !isVisible(step.conditions, step.conditionCombinator, values);
    // Advisor-sourced fields on a step this audience DOES see. Same call the renderer
    // and the completeness walkers make, so the set cannot drift from what was shown.
    const withheld = withheldFieldIds(step, audience);
    // Reveal targets borrowed by an unticked Radio/Checkbox option. They render
    // nowhere but carry no conditions of their own (the owning option holds
    // their visibility), so the condition checks below cannot catch them —
    // exactly the same gap `buildApplicationSnapshot` closes with this call.
    const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);

    for (const section of step.sections) {
      const sectionHidden =
        stepHidden || !isVisible(section.conditions, section.conditionCombinator, values);
      const repeatable = section.repeatable === true;
      // Bounded by what the filler actually reads: `collectFilledFields` sizes
      // its own instance loop with this same call, so an index beyond the
      // current count can never reach a PDF box and needs no pruning.
      const instanceCount = repeatable
        ? getInstanceCount(values, section.id, sectionMinInstances(section))
        : 1;
      // The sentinel describes a block that is no longer there.
      if (sectionHidden && repeatable) drop.add(instanceCountKey(section.id));

      for (const row of section.rows) {
        for (const col of row.columns) {
          const field = col.field;
          if (!field) continue;
          // Layout blocks (StaticText / Divider / Notice / …) hold no answer.
          if (isLayoutBlockType(field.type)) continue;
          const borrowedHidden = hiddenBorrowed.has(field.id);
          const audienceHidden = withheld.has(field.id);

          for (let i = 0; i < instanceCount; i++) {
            const hidden =
              sectionHidden ||
              borrowedHidden ||
              audienceHidden ||
              !isVisible(
                field.conditions,
                field.conditionCombinator,
                // Conditions inside a repeatable section are answered PER
                // INSTANCE — beneficiary 1 may have opened the custodial-parent
                // fields while beneficiary 2 left them closed — so each instance
                // is judged on its own values, matching how FormRenderer decides
                // what to draw. Evaluating against the bare map would prune
                // instance 2's answers on instance 1's reading.
                repeatable ? scopeValuesToInstance(section, values, i, allSections) : values,
              );
            if (!hidden) continue;
            collectFieldKeys(drop, field, section.id, i, repeatable);
          }
        }
      }
    }
  }

  if (drop.size === 0) return values;
  const out: Record<string, FieldValue> = {};
  for (const [key, value] of Object.entries(values)) {
    if (!drop.has(key)) out[key] = value;
  }
  return out;
}

/**
 * Every value key one hidden field can be holding an answer under.
 *
 * A field inside a repeatable section stores one answer per instance at
 * `inst:<sectionId>:<idx>:<fieldId>`; everything else uses the bare field id.
 * A RepeatableRow additionally carries its columns' dual-unit toggle state,
 * which `collectFilledFields` maps into "$ or %" checkboxes — a hidden table
 * must not tick those either. That key is per COLUMN, not per instance (the
 * toggle governs the whole column), so it is composed from the bare field id.
 */
function collectFieldKeys(
  drop: Set<string>,
  field: WorkflowField,
  sectionId: string,
  instanceIdx: number,
  repeatable: boolean,
) {
  drop.add(repeatable ? qualifiedKey(sectionId, instanceIdx, field.id) : field.id);
  if (field.type === 'RepeatableRow') {
    for (const column of field.repeatableTemplate ?? []) {
      drop.add(unitModeKey(field.id, column.id));
    }
  }
}

/** Whether a conditions block is currently visible. Thin alias over
 *  {@link evaluateConditions} (which already treats empty/absent conditions as
 *  visible) that reads clearly at the step/section/field call sites. Mirrors the
 *  helper of the same name in `buildApplicationSnapshot.ts` — the two walks are
 *  meant to agree. */
function isVisible(
  conditions: Parameters<typeof evaluateConditions>[0],
  combinator: Parameters<typeof evaluateConditions>[1],
  values: Record<string, FieldValue>,
): boolean {
  return evaluateConditions(conditions, combinator, values);
}
